import React, { useState, useEffect, useRef } from 'react';
import { X, Settings } from 'lucide-react';
import './MockupEditor.css';

interface LogoVariant {
  key: string;
  label: string;
  url: string | undefined;
}

interface MockupConfig {
  // Center point as percentage of image dimensions
  centerPercent: { x: number; y: number };
  // Base rotation angle (degrees)
  baseRotation: number;
  // Default logo width as percentage of container
  defaultWidthPercent: number;
  // Base image dimensions (for reference)
  imageDimensions: { width: number; height: number };
}

// Predefined mockup configurations - positions as percentages
// Based on the guide coordinates and image dimensions
const MOCKUP_CONFIGS: Record<string, MockupConfig> = {
  mobile: {
    // Image: 1419x799, center around (693, 341)
    centerPercent: { x: 48.8, y: 42.7 },
    baseRotation: -38,
    defaultWidthPercent: 25,
    imageDimensions: { width: 1419, height: 799 },
  },
  tshirt: {
    // Image: 1419x799, center around (932, 414)
    centerPercent: { x: 65.7, y: 51.8 },
    baseRotation: 15,
    defaultWidthPercent: 20,
    imageDimensions: { width: 1419, height: 799 },
  },
  pen: {
    // Image: 1419x799, center around (880, 429)
    centerPercent: { x: 62, y: 53.7 },
    baseRotation: 22,
    defaultWidthPercent: 28,
    imageDimensions: { width: 1419, height: 799 },
  },
  mug: {
    // Image: 1408x768, center on the mug face (horizontally centered on cup)
    centerPercent: { x: 36, y: 52 },
    baseRotation: 0,
    defaultWidthPercent: 15,
    imageDimensions: { width: 1408, height: 768 },
  },
};

interface MockupEditorProps {
  mockupType: 'mobile' | 'tshirt' | 'pen' | 'mug';
  baseImageUrl: string;
  logoVariants: LogoVariant[];
  defaultLogoKey?: string;
  label: string;
  isEditing: boolean;
  position: 'left' | 'right'; // Added position prop
  verticalAlign?: 'top' | 'bottom'; // Control vertical alignment of sidebar
  // Callback when settings change (for saving to brand guide)
  onSettingsChange?: (settings: MockupSettings) => void;
  // Initial settings (loaded from brand guide)
  initialSettings?: MockupSettings;
}

export interface MockupSettings {
  selectedLogoKey: string;
  tilt: number;      // degrees, -45 to +45
  scaleX: number;    // percentage, 50 to 150
  scaleY: number;    // percentage, 50 to 150
  offsetX: number;   // percentage offset, -20 to +20
  offsetY: number;   // percentage offset, -20 to +20
  skewX: number;     // degrees, -30 to +30
  skewY: number;     // degrees, -30 to +30
  perspectiveTilt: number;  // 3D tilt, -30 to +30
}

const DEFAULT_SETTINGS: MockupSettings = {
  selectedLogoKey: 'logo_url',
  tilt: 0,
  scaleX: 100,
  scaleY: 100,
  offsetX: 0,
  offsetY: 0,
  skewX: 0,
  skewY: 0,
  perspectiveTilt: 0,
};

const MockupEditor: React.FC<MockupEditorProps> = ({
  mockupType,
  baseImageUrl,
  logoVariants,
  defaultLogoKey = 'logo_url',
  label,
  isEditing,
  onSettingsChange,
  initialSettings,
  position, // Destructure position prop
  verticalAlign = 'top', // Default to top
}) => {
  const [settings, setSettings] = useState<MockupSettings>(
    initialSettings || { ...DEFAULT_SETTINGS, selectedLogoKey: defaultLogoKey }
  );
  const [showControls, setShowControls] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const config = MOCKUP_CONFIGS[mockupType];

  // Get the selected logo URL - fall back through variants if selected not available
  const getLogoUrl = (): string | undefined => {
    const selected = logoVariants.find(v => v.key === settings.selectedLogoKey);
    if (selected?.url) return selected.url;
    
    // Fall back to any available logo
    const fallback = logoVariants.find(v => v.url);
    return fallback?.url;
  };
  
  const logoUrl = getLogoUrl();

  // Calculate transform style for the logo overlay using percentages
  const getLogoTransformStyle = (): React.CSSProperties => {
    const totalRotation = config.baseRotation + settings.tilt;
    const posX = config.centerPercent.x + settings.offsetX;
    const posY = config.centerPercent.y + settings.offsetY;
    const skewX = settings.skewX || 0;
    const skewY = settings.skewY || 0;
    const perspectiveTilt = settings.perspectiveTilt || 0;
    
    // Build transform string with all adjustments
    const transforms = [
      'translate(-50%, -50%)',
      `rotate(${totalRotation}deg)`,
      `skewX(${skewX}deg)`,
      `skewY(${skewY}deg)`,
      `scaleX(${settings.scaleX / 100})`,
      `scaleY(${settings.scaleY / 100})`,
    ];
    
    // Add 3D perspective tilt if set
    const style: React.CSSProperties = {
      position: 'absolute',
      left: `${posX}%`,
      top: `${posY}%`,
      transform: transforms.join(' '),
      transformOrigin: 'center center',
      width: `${config.defaultWidthPercent}%`,
      pointerEvents: 'none',
    };
    
    // Apply perspective for 3D effect
    if (perspectiveTilt !== 0) {
      style.transform = `perspective(500px) rotateX(${perspectiveTilt}deg) ${transforms.join(' ')}`;
    }
    
    return style;
  };

  // Keep track of the latest callback to avoid effect dependencies
  const onSettingsChangeRef = useRef(onSettingsChange);
  useEffect(() => {
    onSettingsChangeRef.current = onSettingsChange;
  }, [onSettingsChange]);

  // Notify parent of settings changes
  useEffect(() => {
    if (onSettingsChangeRef.current) {
      onSettingsChangeRef.current(settings);
    }
  }, [settings]);

  const handleSettingChange = (key: keyof MockupSettings, value: number | string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setSettings({ ...DEFAULT_SETTINGS, selectedLogoKey: settings.selectedLogoKey });
  };

  // Filter out variants without URLs
  const availableVariants = logoVariants.filter(v => v.url);

  return (
    <div className={`mockup-editor ${showControls ? 'controls-open' : ''} mockup-position-${position} mockup-align-${verticalAlign}`}>
      <div className="mockup-preview-container" ref={containerRef}>
        {/* Base mockup image */}
        <img 
          src={baseImageUrl} 
          alt={`${label} Mockup`}
          className="mockup-base-image"
        />
        
        {/* Logo overlay with transforms */}
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className="mockup-logo-overlay"
            style={getLogoTransformStyle()}
          />
        ) : (
          <div className="mockup-no-logo" style={{
            position: 'absolute',
            left: `${config.centerPercent.x}%`,
            top: `${config.centerPercent.y}%`,
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontSize: '12px',
            background: 'rgba(255,255,255,0.8)',
            padding: '4px 8px',
            borderRadius: '4px',
          }}>
            No logo uploaded
          </div>
        )}

        {/* Edit button */}
        {isEditing && (
          <button 
            className="mockup-edit-btn"
            onClick={() => setShowControls(!showControls)}
          >
            {showControls ? <X size={16} /> : <Settings size={16} />}
          </button>
        )}
      </div>

      {/* Controls panel - positioned to the side */}
      {isEditing && showControls && (
        <div className="mockup-controls-sidebar">
          <div className="control-header">
            <span>Customize Mockup</span>
            <button className="reset-btn" onClick={handleReset}>Reset</button>
          </div>

          {/* Logo Selection */}
          <div className="control-group">
            <label>Logo Variant</label>
            <select
              value={settings.selectedLogoKey}
              onChange={(e) => handleSettingChange('selectedLogoKey', e.target.value)}
            >
              {availableVariants.map(variant => (
                <option key={variant.key} value={variant.key}>
                  {variant.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tilt */}
          <div className="control-group">
            <label>Tilt: {settings.tilt}°</label>
            <input
              type="range"
              min="-45"
              max="45"
              value={settings.tilt}
              onChange={(e) => handleSettingChange('tilt', Number(e.target.value))}
            />
          </div>

          {/* Scale X */}
          <div className="control-group">
            <label>Width: {settings.scaleX}%</label>
            <input
              type="range"
              min="50"
              max="150"
              value={settings.scaleX}
              onChange={(e) => handleSettingChange('scaleX', Number(e.target.value))}
            />
          </div>

          {/* Scale Y */}
          <div className="control-group">
            <label>Height: {settings.scaleY}%</label>
            <input
              type="range"
              min="50"
              max="150"
              value={settings.scaleY}
              onChange={(e) => handleSettingChange('scaleY', Number(e.target.value))}
            />
          </div>

          {/* Offset X */}
          <div className="control-group">
            <label>Horizontal: {settings.offsetX > 0 ? '+' : ''}{settings.offsetX}%</label>
            <input
              type="range"
              min="-20"
              max="20"
              step="0.5"
              value={settings.offsetX}
              onChange={(e) => handleSettingChange('offsetX', Number(e.target.value))}
            />
          </div>

          {/* Offset Y */}
          <div className="control-group">
            <label>Vertical: {settings.offsetY > 0 ? '+' : ''}{settings.offsetY}%</label>
            <input
              type="range"
              min="-20"
              max="20"
              step="0.5"
              value={settings.offsetY}
              onChange={(e) => handleSettingChange('offsetY', Number(e.target.value))}
            />
          </div>

          <div className="control-divider"></div>
          <div className="control-section-label">Skew & Perspective</div>

          {/* Skew X */}
          <div className="control-group">
            <label>Skew H: {settings.skewX || 0}°</label>
            <input
              type="range"
              min="-30"
              max="30"
              value={settings.skewX || 0}
              onChange={(e) => handleSettingChange('skewX', Number(e.target.value))}
            />
          </div>

          {/* Skew Y */}
          <div className="control-group">
            <label>Skew V: {settings.skewY || 0}°</label>
            <input
              type="range"
              min="-30"
              max="30"
              value={settings.skewY || 0}
              onChange={(e) => handleSettingChange('skewY', Number(e.target.value))}
            />
          </div>

          {/* Perspective Tilt */}
          <div className="control-group">
            <label>3D Tilt: {settings.perspectiveTilt || 0}°</label>
            <input
              type="range"
              min="-30"
              max="30"
              value={settings.perspectiveTilt || 0}
              onChange={(e) => handleSettingChange('perspectiveTilt', Number(e.target.value))}
            />
          </div>
        </div>
      )}

      <span className="mockup-label">{label}</span>
    </div>
  );
};

export default MockupEditor;
